// schemas
import { SCHEMA_KEYS } from 'schemas/chartConfig'
import { ChartSchemaNode, JsonSchemaType } from './schema'
// types
import { ChartType } from 'types/charts'
// utils
import { startCase } from 'lodash'

export const isVisibleFor = (node: ChartSchemaNode, chartType: ChartType) =>
  !node[SCHEMA_KEYS.chartTypes] || node[SCHEMA_KEYS.chartTypes].includes(chartType)

export const getVisibleEntries = (node: ChartSchemaNode, chartType: ChartType) =>
  Object.entries(node.properties || {}).filter(([, child]) => isVisibleFor(child, chartType))

const isNull = (node: ChartSchemaNode) => node.type === JsonSchemaType.Null

// zod writes a nullable field as anyOf [the field, null]: one node with the field's own rules
export const unwrapNullable = (node: ChartSchemaNode): ChartSchemaNode & { nullable?: boolean } => {
  const value = node.anyOf?.length === 2 && node.anyOf.some(isNull) && node.anyOf.find((item) => !isNull(item))

  return value ? { ...node, ...value, nullable: true } : node
}

export const isNumber = (node: ChartSchemaNode) =>
  node.type === JsonSchemaType.Number || node.type === JsonSchemaType.Integer

export const getFieldLabel = (name: string, node: ChartSchemaNode) => node.title || startCase(name.split('.').pop())

// 'NONE' → 'None', 'NAME_VALUE' → 'Name Value'
export const getEnumLabel = (value: string | number) => startCase(String(value).toLowerCase())

// An empty input clears a nullable number and falls back to the default for the others
export const parseNumber = (nullable: boolean) => (value: string) => {
  // A blur gives no value (Input calls it without the event): redux-form then keeps the field as it is, where null
  // would clear it
  if (value === undefined) {
    return undefined
  }

  if (value === '' || value === null) {
    return nullable ? null : undefined
  }

  const number = Number(value)

  return Number.isNaN(number) ? undefined : number
}

// What the schema refuses, said in the form before the server refuses it as CHART_CONFIG_INVALID
export const getSchemaValidator = (node: ChartSchemaNode) => (value: unknown) => {
  if (typeof value === 'number' && isNumber(node)) {
    if (node.minimum !== undefined && value < node.minimum) {
      return `At least ${node.minimum}`
    }
    if (node.maximum !== undefined && value > node.maximum) {
      return `At most ${node.maximum}`
    }
  }

  if (typeof value === 'string' && node.maxLength !== undefined && value.length > node.maxLength) {
    return `At most ${node.maxLength} characters`
  }

  return undefined
}
